import React, { useState } from "react"

import { useTranslation } from "react-i18next"
import { useQuery } from "react-query"
import { useParams } from "react-router-dom"

import LaunchOutlinedIcon from "@mui/icons-material/LaunchOutlined"
import TagIcon from "@mui/icons-material/Tag"
import { Grid } from "@mui/material"
import { Button } from "@synapse-analytics/synapse-ui"
import type { AxiosError, AxiosResponse } from "axios"

import { ValueBlock } from "../../../../components/data-display/ValueBlock"
import { getTheme } from "../../../../hooks/UseTheme"
import { KonanAPI } from "../../../../services/KonanAPI"
import type { PaginatedTagListListList } from "../../../../types/generated/api/PaginatedTagListListList"
import type { TagListList } from "../../../../types/generated/api/TagListList"
import { useNodeDeletion } from "../../hooks/useNodeHooks"
import type { SelectionDataItemType, TaglistNodeProps } from "../../types/interfaces"
import { nodeHelpers } from "../../utils/nodeHelpers"
import { SelectDataMenu } from "./SelectDataMenu"
import { BaseWorkflowNode } from "./base/BaseWorkflowNode"

/**
 * TaglistNode component handles tag list selection and configuration in workflows.
 * Supports tag list selection and external navigation.
 */
export function TaglistNode(props: Readonly<TaglistNodeProps>): React.ReactElement {
  const { data, menuView, id } = props
  const updateGraph = props.data?.updateGraph
  const taglists = data?.taglists
  const readMode: boolean | null = data?.readMode ?? null
  const nodeId = id ?? null
  const currentDataItemId = data?.taglist

  // Navigation hooks
  const { id: projectId } = useParams<{ id: string }>()

  // Theme hook
  const theme = getTheme()

  // Translation hook
  const { t } = useTranslation("workflows", {
    keyPrefix: "selectionNode",
  })

  // Constants
  const page = 1
  const pageSize = 200

  // Get node metadata
  const metadata = nodeHelpers.getNodeMetadata("TaglistNode")

  // Use our custom hook for node deletion
  const { handleRemoveNode } = useNodeDeletion(nodeId as string, updateGraph)

  // Data fetching queries
  const { isLoading: isTaglistsLoading, data: taglistsData } = useQuery<
    AxiosResponse<PaginatedTagListListList>,
    AxiosError
  >(["taglists", projectId, page, pageSize], () => KonanAPI.fetchTagLists(projectId as string, page, pageSize), {
    enabled: !!projectId && !menuView && !readMode && data?.nodeType === "TaglistNode",
  })

  const currentData = (): PaginatedTagListListList | undefined => {
    return taglistsData?.data ?? taglists
  }

  const [selectedValue, setSelectedValue] = useState<SelectionDataItemType | undefined | string>(
    currentDataItemId
      ? typeof currentDataItemId === "string"
        ? currentDataItemId
        : (currentDataItemId as TagListList)?.uuid
      : undefined,
  )

  return (
    <BaseWorkflowNode
      id={nodeId as string}
      menuView={menuView}
      readMode={readMode ?? undefined}
      nodeType="TaglistNode"
      title={t("nodeTitles.taglist")}
      iconProps={{ element: <TagIcon sx={{ color: theme.palette.base.text[1] }} />, className: metadata.iconClassName }}
      width={metadata.width}
      handlesProps={{ showTarget: metadata.showTargetHandle, showSource: metadata.showSourceHandle }}
      onRemove={() => handleRemoveNode(t("notifications.nodeRemoved", { nodeType: "taglist" }))}
    >
      {/* Node Content */}
      <Grid item xs={12} display="flex" justifyContent="space-between" alignItems="center">
        {/* Data Selection */}
        <Grid item xs={10}>
          {!menuView && !readMode ? (
            <SelectDataMenu
              nodeId={nodeId}
              dataType="TaglistNode"
              dataItemId={currentDataItemId}
              menuView={menuView}
              itemData={currentData()}
              selectedValue={selectedValue as string}
              setSelectedValue={setSelectedValue}
              isDataLoading={isTaglistsLoading}
            />
          ) : (
            <Grid container justifyContent="space-between" alignItems="center">
              <ValueBlock
                value={
                  typeof currentDataItemId === "string"
                    ? currentDataItemId
                    : (currentDataItemId as TagListList)?.name || t("nodeBodies.noTaglistSelected")
                }
                size={12}
                showTooltip={!menuView}
              />
            </Grid>
          )}
        </Grid>

        {/* Action Button - External Link */}
        <Grid item xs={2} ml={0.5}>
          <Button
            onClick={() =>
              setTimeout(() => {
                window.open(`/projects/${projectId}/Taglists`, "_blank")
              })
            }
            disabled={menuView}
            size={"regular"}
            fullWidth
            data-mode={readMode ? "read" : menuView ? "edit-view" : "edit"}
            variant="secondary"
          >
            <LaunchOutlinedIcon fontSize="small" sx={{ color: theme.palette.base.text[1] }} />
          </Button>
        </Grid>
      </Grid>
    </BaseWorkflowNode>
  )
}
